import {
	CLOB_API_URL,
	POLYGON_CHAIN_ID,
	RELAYER_FALLBACK_URL,
	RELAYER_URL,
	REMOTE_SIGNING_URL,
} from '@/constants/polymarket'
import { isRateLimitError } from '@/lib/relayer-fallback'
import { useWallet } from '@/providers/WalletContext'
import { checkAllApprovals, createAllApprovalTxs } from '@/utils/approvals'
import { deriveSafeAddress } from '@/utils/safe'
import { type TradingSession, loadSession, saveSession } from '@/utils/session'
import { RelayClient, RelayerTransactionState } from '@polymarket/builder-relayer-client'
import { BuilderConfig } from '@polymarket/builder-signing-sdk'
import { ClobClient } from '@polymarket/clob-client'
import { useCallback, useEffect, useRef, useState } from 'react'

export type TradingStatus = 'disconnected' | 'initializing' | 'ready' | 'error'

export interface PolymarketTradingState {
	status: TradingStatus
	currentAction: string | null
	error: Error | null
	clobClient: ClobClient | null
	relayClient: RelayClient | null
	safeAddress: string | undefined
	eoaAddress: string | undefined
	retry: () => void
}

export default function usePolymarketTrading(): PolymarketTradingState {
	const [status, setStatus] = useState<TradingStatus>('disconnected')
	const [currentAction, setCurrentAction] = useState<string | null>(null)
	const [error, setError] = useState<Error | null>(null)
	const [clobClient, setClobClient] = useState<ClobClient | null>(null)
	const [relayClient, setRelayClient] = useState<RelayClient | null>(null)
	const [_session, setSession] = useState<TradingSession | null>(null)

	const isInitializingRef = useRef(false)

	const { eoaAddress, walletClient, ethersSigner, publicClient } = useWallet()
	const safeAddress = eoaAddress ? deriveSafeAddress(eoaAddress) : undefined

	const retry = useCallback(() => {
		setError(null)
		setStatus('disconnected') // This triggers the auto-init effect
	}, [])

	const initialize = useCallback(async () => {
		if (!(eoaAddress && walletClient && ethersSigner && safeAddress)) {
			return
		}

		if (isInitializingRef.current) {
			return
		}
		isInitializingRef.current = true

		setStatus('initializing')
		setError(null)

		try {
			setCurrentAction('Checking wallet...')
			const builderConfig = new BuilderConfig({
				remoteBuilderConfig: { url: REMOTE_SIGNING_URL() },
			})

			// Runs all relay-dependent steps with the given URL.
			// Throws on non-429 errors; returns the relay client on success.
			const initRelayOperations = async (relayerUrl: string) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const relay = new RelayClient(
					relayerUrl,
					POLYGON_CHAIN_ID,
					walletClient as any,
					builderConfig
				)

				// Check/deploy Safe
				let isDeployed = false
				try {
					// biome-ignore lint/suspicious/noExplicitAny: getDeployed method not in type definition
					isDeployed = await (relay as any).getDeployed(safeAddress)
				} catch {
					const code = await publicClient?.getCode({
						address: safeAddress as `0x${string}`,
					})
					isDeployed = !!code && code !== '0x'
				}

				if (!isDeployed) {
					setCurrentAction('Deploying wallet...')
					const response = await relay.deploy()
					await relay.pollUntilState(
						response.transactionID,
						[
							RelayerTransactionState.STATE_MINED,
							RelayerTransactionState.STATE_CONFIRMED,
							RelayerTransactionState.STATE_FAILED,
						],
						'60',
						3000
					)
				}

				// Set approvals
				setCurrentAction('Setting approvals...')
				const approvalStatus = await checkAllApprovals(safeAddress)
				if (!approvalStatus.allApproved) {
					const approvalTxs = createAllApprovalTxs()
					const response = await relay.execute(approvalTxs, 'Set token approvals for trading')
					await response.wait()
				}

				return relay
			}

			// Try primary relayer, fall back on 429
			let relay: RelayClient
			try {
				relay = await initRelayOperations(RELAYER_URL)
			} catch (relayErr) {
				if (isRateLimitError(relayErr)) {
					console.warn('Relayer returned 429, switching to fallback:', RELAYER_FALLBACK_URL)
					setCurrentAction('Switching relayer...')
					relay = await initRelayOperations(RELAYER_FALLBACK_URL)
				} else {
					throw relayErr
				}
			}
			setRelayClient(relay)

			// Get API credentials (use cached if available for instant return)
			setCurrentAction('Getting credentials...')
			const stored = loadSession(eoaAddress)
			let apiCreds = stored?.apiCredentials
			if (!(apiCreds?.key && apiCreds?.secret && apiCreds?.passphrase)) {
				// biome-ignore lint/suspicious/noExplicitAny: ClobClient expects Ethers v5 Signer
				const tempClient = new ClobClient(CLOB_API_URL, POLYGON_CHAIN_ID, ethersSigner as any)
				apiCreds = await tempClient.deriveApiKey().catch(() => undefined)
				if (!(apiCreds?.key && apiCreds?.secret && apiCreds?.passphrase)) {
					apiCreds = await tempClient.createApiKey()
				}
			}

			// Create authenticated ClobClient
			const authenticatedClient = new ClobClient(
				CLOB_API_URL,
				POLYGON_CHAIN_ID,
				// biome-ignore lint/suspicious/noExplicitAny: ClobClient expects Ethers v5 Signer
				ethersSigner as any,
				apiCreds,
				2, // signatureType for EOA signing for Safe
				safeAddress,
				undefined,
				false,
				builderConfig
			)
			setClobClient(authenticatedClient)

			// Save session and complete
			const newSession: TradingSession = {
				eoaAddress,
				safeAddress,
				isSafeDeployed: true,
				hasApiCredentials: true,
				hasApprovals: true,
				apiCredentials: apiCreds,
				lastChecked: Date.now(),
			}
			setSession(newSession)
			saveSession(eoaAddress, newSession)

			setCurrentAction(null)
			setStatus('ready')
		} catch (err) {
			console.error('Trading initialization error:', err)
			setError(err instanceof Error ? err : new Error('Failed to initialize trading'))
			setCurrentAction(null)
			setStatus('error')
		} finally {
			isInitializingRef.current = false
		}
	}, [eoaAddress, walletClient, ethersSigner, safeAddress, publicClient])

	// Auto-initialize when wallet connects
	useEffect(() => {
		if (!eoaAddress) {
			setStatus('disconnected')
			setClobClient(null)
			setRelayClient(null)
			setSession(null)
			setCurrentAction(null)
			setError(null)
			return
		}

		// Check for existing session
		const stored = loadSession(eoaAddress)
		if (stored) {
			setSession(stored)
		}

		// Start initialization if not already ready
		if (status === 'disconnected') {
			initialize()
		}
	}, [eoaAddress, status, initialize])

	return {
		status,
		currentAction,
		error,
		clobClient,
		relayClient,
		safeAddress,
		eoaAddress,
		retry,
	}
}
